import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { L10n } from "@fidget/common/i18n";
import { NavigationButton } from "../../components/navigationButton";
import { NavButtonGroup } from "../../components/settings/navButtonGroup";
import { useStore } from "../../models";
import { Path } from "../../models/router";
import { DashboardView } from "../dashboard/views/DashboardView";
import styles from "./settingsNavigation.css";
import { AppPermission } from "@closerplatform/spinner-openapi";

interface Props {
  view: DashboardView;
}

export const SettingsNavigation = observer(({ view }: Props) => {
  const { uiStore, currentUserStore } = useStore();
  const { planPrice } = currentUserStore;

  const canAccessSettingsGeneral = uiStore.canDisplayWithPermission(AppPermission.SettingsGeneral);

  const getAdvisersMainLink = () => {
    if (
      uiStore.canDisplayAdvisersConfiguration() ||
      uiStore.canDisplayAdvisersAssignments() ||
      uiStore.canDisplayAgentGroupsConfiguration()
    ) {
      return Path.SETTINGS_USERS;
    }
    if (uiStore.canDisplayAgentGroupsConfiguration()) {
      return Path.SETTINGS_ADVISER_GROUPS;
    }
    if (uiStore.canDisplayStatuses()) {
      return Path.SETTINGS_ADVISER_STATUSES;
    }

    return Path.SETTINGS_ADVISER_AVATAR;
  };

  const renderAdminNavButtons = () => {
    return (
      <>
        {uiStore.canDisplayAdvisersSettings() && (
          <NavButtonGroup
            labelId="settingsNavGroup.advisers"
            currentView={view}
            groupViews={[
              DashboardView.SETTINGS_USERS,
              DashboardView.SETTINGS_ADVISER_GROUPS,
              DashboardView.SETTINGS_ADVISER_STATUSES,
              DashboardView.SETTINGS_ADVISER_AVATAR
            ]}
            linkTo={getAdvisersMainLink()}
          >
            {(uiStore.canDisplayAdvisersConfiguration() ||
              uiStore.canDisplayAdvisersAssignments() ||
              uiStore.canDisplayAgentGroupsConfiguration() ||
              uiStore.canDisplayAdvisersAutoAssignedLimit()) && (
              <NavigationButton
                currentView={view}
                desiredView={DashboardView.SETTINGS_USERS}
                linkTo={Path.SETTINGS_USERS}
                l10nId={"settings.advisers.list"}
              />
            )}
            {(uiStore.canDisplayAgentGroupsConfiguration() ||
              uiStore.canDisplayAdvisersAssignments()) && (
              <NavigationButton
                currentView={view}
                desiredView={DashboardView.SETTINGS_ADVISER_GROUPS}
                linkTo={Path.SETTINGS_ADVISER_GROUPS}
                l10nId={"settings.adviserGroups"}
              />
            )}
            {uiStore.canDisplayStatuses() && (
              <NavigationButton
                currentView={view}
                desiredView={DashboardView.SETTINGS_ADVISER_STATUSES}
                linkTo={Path.SETTINGS_ADVISER_STATUSES}
                l10nId={"settings.adviserStatuses"}
              />
            )}
            {uiStore.canDisplayWithPermission(AppPermission.SettingsAdvisersAvatar) && (
              <NavigationButton
                currentView={view}
                desiredView={DashboardView.SETTINGS_ADVISER_AVATAR}
                linkTo={Path.SETTINGS_ADVISER_AVATAR}
                l10nId={"settings.adviserAvatar"}
              />
            )}
          </NavButtonGroup>
        )}
        {canAccessSettingsGeneral && !!planPrice && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_BILLING}
            linkTo={Path.SETTINGS_BILLING}
            l10nId={"settings.billing"}
          />
        )}
        {canAccessSettingsGeneral && (
          <NavButtonGroup
            currentView={view}
            linkTo={Path.SETTINGS_ASSIGNMENT_RULES}
            groupViews={[
              DashboardView.SETTINGS_ASSIGNMENT_RULES,
              DashboardView.SETTINGS_MANUAL_ASSIGNMENT
            ]}
            labelId="settings.admin.conversations"
          >
            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_ASSIGNMENT_RULES}
              linkTo={Path.SETTINGS_ASSIGNMENT_RULES}
              l10nId={"settings.admin.assignmentRules"}
            />
            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_MANUAL_ASSIGNMENT}
              linkTo={Path.SETTINGS_MANUAL_ASSIGNMENT}
              l10nId={"settings.admin.manualAssingment"}
            />
          </NavButtonGroup>
        )}
        {canAccessSettingsGeneral && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_BOTS}
            linkTo={Path.SETTINGS_BOTS}
            l10nId={"settings.admin.bots"}
          />
        )}
        {canAccessSettingsGeneral && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_WIDGET_CONFIG}
            linkTo={Path.SETTINGS_WIDGET}
            l10nId={"settings.admin.widgetConfig"}
          />
        )}
        {uiStore.canDisplayWithPermission(AppPermission.SettingsWebhooks) && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_WEBHOOKS}
            linkTo={Path.SETTINGS_WEBHOOKS}
            l10nId={"settings.admin.webhooks"}
          />
        )}
        {canAccessSettingsGeneral && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_MESSAGES}
            linkTo={Path.SETTINGS_MESSAGES}
            l10nId={"settings.admin.messages"}
          />
        )}
        {uiStore.canDisplayWithPermission(AppPermission.SettingsProactiveMessages) && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_PROACTIVE_MESSAGES}
            linkTo={Path.SETTINGS_PROACTIVE_MESSAGES}
            l10nId={"settings.admin.proactiveMessages"}
          />
        )}
        {canAccessSettingsGeneral && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_REPORTS}
            linkTo={Path.SETTINGS_REPORTS}
            l10nId={"settings.admin.reports"}
          />
        )}

        {uiStore.canDisplayWithPermission(AppPermission.SettingsRoles) && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_ROLES}
            linkTo={Path.SETTINGS_ROLES}
            l10nId={"settings.rolesSettings"}
          />
        )}

        {uiStore.canDisplayWithPermission(AppPermission.SettingsTags) && (
          <NavButtonGroup
            labelId="tags.title"
            currentView={view}
            groupViews={[
              DashboardView.SETTINGS_TAGS,
              DashboardView.SETTINGS_TAGGING,
              DashboardView.SETTINGS_TAG_GROUPS
            ]}
            linkTo={Path.SETTINGS_TAGS}
          >
            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_TAGS}
              linkTo={Path.SETTINGS_TAGS}
              l10nId={"settings.admin.tags"}
            />

            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_TAGGING}
              linkTo={Path.SETTINGS_TAGGING}
              l10nId={"settings.admin.tagging"}
            />

            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_TAG_GROUPS}
              linkTo={Path.SETTINGS_TAG_GROUPS}
              l10nId={"settings.admin.tagGroups"}
            />
          </NavButtonGroup>
        )}

        {canAccessSettingsGeneral && (
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_WORKING_HOURS}
            linkTo={Path.SETTINGS_WORKING_HOURS}
            l10nId={"settings.admin.workingHours"}
          />
        )}
      </>
    );
  };

  const renderAdminNavSection = () => {
    const buttons = renderAdminNavButtons();
    const canAccessAnyAdminSettingsTab = buttons.props.children.find((c: any) => !!c);

    return (
      canAccessAnyAdminSettingsTab && (
        <div className={styles["nav-section"]}>
          <div className={styles["nav-section-title"]}>
            <L10n id={"settings.adminSettings"} />
          </div>
          {buttons}
        </div>
      )
    );
  };

  const render = () => {
    return (
      <div
        className={classNames(styles["navigation-container"], {
          [styles["margin-onboarding-btn"]]: uiStore.userflowButtonPresent
        })}
      >
        <div className={classNames(styles["nav-section"], styles["not-last"])}>
          <div className={styles["nav-section-title"]}>
            <L10n id={"settings.personalSettings.title"} />
          </div>
          <NavigationButton
            currentView={view}
            desiredView={DashboardView.SETTINGS_PROFILE}
            linkTo={Path.SETTINGS_PROFILE}
            l10nId={"settings.personalSettings.profile"}
          />
          {uiStore.canDisplayPersonalSettings() && (
            <NavigationButton
              currentView={view}
              desiredView={DashboardView.SETTINGS_SETTINGS}
              linkTo={Path.SETTINGS_SETTINGS}
              l10nId={"settings.settingsGoogle"}
            />
          )}
        </div>
        {renderAdminNavSection()}
      </div>
    );
  };

  return render();
});
